import React, { useContext, useMemo, useState } from "react"
import AdHocCommands from "../../api/adHocCommands"
import { XmppContext } from "../../stream/xmppClient"
import { useAppSelector } from "../../reducers/hooks"
import { RootState } from "../../reducers/store"
import { selectAccountChats } from "../../reducers/accountChatsSlice"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

export const TRANSICS_DRIVER_GROUP = "transics_driver"

type AddTransicsCarModalProps = {
  isOpen: boolean
  onClose: () => void
}

const AddTransicsCarModal = ({ isOpen, onClose }: AddTransicsCarModalProps) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const cars = useAppSelector((state: RootState) => Object.values(state.cars))
  const accountChats = useAppSelector(selectAccountChats)

  const [selectedCarId, setSelectedCarId] = useState("")
  const [name, setName] = useState("")
  const [carSelectorOpen, setCarSelectorOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const availableCars = useMemo(() => {
    const usedCarIds = new Set(
      Object.values(accountChats)
        .filter((chat) => chat.group === TRANSICS_DRIVER_GROUP)
        .map((chat) => chat.carId),
    )
    return cars
      .filter((car) => !car.id.startsWith("manual"))
      .filter((car) => !usedCarIds.has(car.id))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [cars, accountChats])

  const selectedCar = availableCars.find((car) => car.id === selectedCarId)

  const resetAndClose = () => {
    setSelectedCarId("")
    setName("")
    setError(null)
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedCarId) return

    setIsSubmitting(true)
    setError(null)
    try {
      await AdHocCommands.createTransicsChat({
        client,
        carId: selectedCarId,
        name: name.trim() || undefined,
      })
      resetAndClose()
    } catch (err) {
      console.error("Failed to create Transics chat", err)
      setError(t("fleet.providerCar.createFailed", { provider: "Transics" }))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent
        className="font-normal sm:max-w-[480px]"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("fleet.providerCar.title", { provider: "Transics" })}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="transicsCar">{t("fleet.providerCar.car")}</Label>
              <Popover
                open={carSelectorOpen}
                onOpenChange={setCarSelectorOpen}
                modal={true}
              >
                <PopoverTrigger asChild>
                  <Button
                    id="transicsCar"
                    variant="outline"
                    role="combobox"
                    aria-expanded={carSelectorOpen}
                    disabled={isSubmitting}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">
                      {selectedCar
                        ? selectedCar.label
                        : t("fleet.providerCar.selectCar")}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
                  sideOffset={5}
                >
                  <Command>
                    <CommandInput placeholder={t("fleet.providerCar.search")} />
                    <CommandList className="max-h-[300px] overflow-y-auto">
                      <CommandEmpty>
                        {t("fleet.providerCar.empty")}
                      </CommandEmpty>
                      <CommandGroup>
                        {availableCars.map((car) => (
                          <CommandItem
                            key={car.id}
                            value={car.label}
                            onSelect={() => {
                              setSelectedCarId(car.id)
                              setCarSelectorOpen(false)
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedCarId === car.id
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {car.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                {t("fleet.providerCar.hint", { provider: "Transics" })}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transicsCarName">
                {t("fleet.providerCar.name")}
              </Label>
              <Input
                id="transicsCarName"
                disabled={isSubmitting}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full"
                placeholder={
                  selectedCar
                    ? t("fleet.providerCar.defaultsTo", {
                        name: selectedCar.label,
                      })
                    : t("fleet.providerCar.overrideName")
                }
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <DialogFooter>
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={resetAndClose}
              >
                {t("common.cancel")}
              </Button>
              <Button
                autoFocus
                type="submit"
                disabled={isSubmitting || !selectedCarId}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? t("fleet.form.saving") : t("common.save")}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddTransicsCarModal
